// A minimal non-blocking "undo toast" — the affordance behind the report pages'
// remove control (T1 report-parity, 2026-10-28). Removing an action/Item from a
// report is a one-click optimistic correction with no confirm dialog; the undo
// toast makes a mis-click recoverable WITHOUT a blocking confirm (operator ask:
// "non-blocking · Undo", not a modal). The report pages had no reusable toast
// (the "reorg-toast" was an optimistic payload echo, not a UI component), so this
// is a small self-contained one — a bottom-centered stack that auto-dismisses.
//
// Contract: the caller has ALREADY applied the removal optimistically (hidden the
// row) and POSTed the un-place. `onUndo` re-applies (re-POST + un-hide) when the
// recipient clicks Undo; `onExpire` finalizes the removal (actually drop the row)
// when the toast times out or is dismissed without an undo. Exactly one of the two
// fires per toast.

export interface UndoToastOptions {
  message: string;
  undoLabel?: string;
  /** Re-apply the removed placement. Awaited; a throw surfaces inline and leaves
   *  the toast open so the recipient can retry. */
  onUndo: () => void | Promise<void>;
  /** Finalize the removal — called once if the toast expires / is dismissed
   *  without an Undo. Never called when Undo succeeds. */
  onExpire?: () => void;
  /** Auto-dismiss delay (ms). Default 6000. */
  durationMs?: number;
}

const CONTAINER_ID = "undo-toast-container";

function ensureContainer(): HTMLElement {
  let c = document.getElementById(CONTAINER_ID);
  if (!c) {
    c = document.createElement("div");
    c.id = CONTAINER_ID;
    c.className = "undo-toast-container";
    // Container is inert; only the toasts inside catch pointer events.
    c.setAttribute("aria-live", "polite");
    document.body.appendChild(c);
  }
  return c;
}

export function showUndoToast(opts: UndoToastOptions): void {
  const { message, onUndo, onExpire } = opts;
  const undoLabel = opts.undoLabel ?? "Undo";
  const durationMs = opts.durationMs ?? 6000;

  const container = ensureContainer();
  const toast = document.createElement("div");
  toast.className = "undo-toast";
  toast.setAttribute("role", "status");

  const msg = document.createElement("span");
  msg.className = "undo-toast-message";
  msg.textContent = message;
  toast.appendChild(msg);

  const undoBtn = document.createElement("button");
  undoBtn.type = "button";
  undoBtn.className = "undo-toast-action";
  undoBtn.textContent = undoLabel;
  toast.appendChild(undoBtn);

  let settled = false;
  let timer: ReturnType<typeof setTimeout> | undefined;

  const close = (): void => {
    if (timer !== undefined) clearTimeout(timer);
    toast.remove();
    // Drop the container once it empties so the DOM stays clean between toasts.
    if (!container.querySelector(".undo-toast")) container.remove();
  };

  const expire = (): void => {
    if (settled) return;
    settled = true;
    close();
    onExpire?.();
  };

  undoBtn.addEventListener("click", async () => {
    if (settled) return;
    undoBtn.disabled = true;
    try {
      await onUndo();
      settled = true; // success → onExpire must NOT fire
      close();
    } catch {
      // Re-apply failed — keep the toast open (reset the timer) and show inline.
      undoBtn.disabled = false;
      let err = toast.querySelector<HTMLElement>(".undo-toast-error");
      if (!err) {
        err = document.createElement("span");
        err.className = "undo-toast-error";
        toast.appendChild(err);
      }
      err.textContent = "Couldn't undo — try again.";
      if (timer !== undefined) clearTimeout(timer);
      timer = setTimeout(expire, durationMs);
    }
  });

  container.appendChild(toast);
  timer = setTimeout(expire, durationMs);
}
